import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ChefHat, Package, BarChart3, LogOut, Menu, X, Store, UtensilsCrossed, Armchair, Pizza, GlassWater, Gift, Award, ClipboardList, FileText, ChevronDown, Wrench, Building2, QrCode } from 'lucide-react';
import { Restaurant, TenantRole } from '../../lib/types';
import { cn } from '../../lib/utils';
import { useInvoiceRequests } from '../../lib/invoice-requests-hooks';
interface AdminLayoutProps {
  restaurant: Restaurant | null;
  role?: TenantRole;
  onSignOut?: () => void;
  children: React.ReactNode;
}
interface NavItem {
  name: string;
  path: string;
  icon: any;
  roles: TenantRole[];
  badge?: number;
}
interface NavGroup {
  type: 'group';
  name: string;
  icon: any;
  roles: TenantRole[];
  items: NavItem[];
  defaultOpen?: boolean;
}
type NavEntry = NavItem | NavGroup;
const isGroup = (entry: NavEntry): entry is NavGroup => (entry as NavGroup).type === 'group';
export function AdminLayout({
  restaurant,
  role = 'admin',
  onSignOut,
  children
}: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    pendingRequests
  } = useInvoiceRequests(restaurant?.id);
  const pendingInvoiceCount = pendingRequests.length;
  // Collapsible group open state — persists per session
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Estaciones: false,
    'Otras Herramientas': false
  });
  if (!restaurant) return null;
  const slug = restaurant.slug;
  // La Cabaña Parrillera (id 24) — only Cocina + Batidos y postres in Estaciones.
  const isCabanaParrillera = String(restaurant.id) === '24' || restaurant.slug === 'la-cabana-parrillera' || restaurant.slug === 'cabana-parrillera';
  // Build the nav structure (in display order)
  const allEntries: NavEntry[] = [{
    name: 'Tomar Orden',
    path: `/admin/${slug}/waiter`,
    icon: ClipboardList,
    roles: ['owner', 'admin', 'manager', 'caja', 'waiter'] as TenantRole[]
  }, {
    name: 'Mesas',
    path: `/admin/${slug}/tables`,
    icon: Armchair,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    type: 'group',
    name: 'Estaciones',
    icon: Building2,
    roles: ['owner', 'admin', 'manager', 'caja', 'cocina', 'pizzeria', 'bebidas'] as TenantRole[],
    items: [{
      name: 'KDS (Todas)',
      path: `/admin/${slug}/kds`,
      icon: ChefHat,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    }, {
      name: 'Cocina',
      path: `/admin/${slug}/kds?station=cocina`,
      icon: ChefHat,
      roles: ['owner', 'admin', 'manager', 'caja', 'cocina'] as TenantRole[]
    },
    // Pizzería — hidden for La Cabaña Parrillera (no pizza station)
    ...(isCabanaParrillera ? [] : [{
      name: 'Pizzería',
      path: `/admin/${slug}/kds?station=pizzeria`,
      icon: Pizza,
      roles: ['owner', 'admin', 'manager', 'caja', 'pizzeria'] as TenantRole[]
    }]), {
      // For La Cabaña: rename to "Batidos y postres" to match the
      // owner-facing terminology. Code/route stay the same (station=bebidas).
      name: isCabanaParrillera ? 'Batidos y postres' : 'Postres y Batidos',
      path: `/admin/${slug}/kds?station=bebidas`,
      icon: GlassWater,
      roles: ['owner', 'admin', 'manager', 'caja', 'bebidas'] as TenantRole[]
    }]
  }, {
    name: 'Control de Caja',
    path: `/admin/${slug}/caja`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Dashboard',
    path: `/admin/${slug}/dashboard`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Menú',
    path: `/admin/${slug}/menu`,
    icon: UtensilsCrossed,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    type: 'group',
    name: 'Otras Herramientas',
    icon: Wrench,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[],
    items: [{
      name: 'Enviar Facturas',
      path: `/admin/${slug}/invoice-requests`,
      icon: FileText,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[],
      badge: pendingInvoiceCount
    }, {
      name: 'Inventario',
      path: `/admin/${slug}/inventory`,
      icon: Package,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    }, {
      name: 'Métricas',
      path: `/admin/${slug}/metrics`,
      icon: BarChart3,
      roles: ['owner', 'admin', 'manager'] as TenantRole[]
    }, {
      name: 'Lealtad',
      path: `/admin/${slug}/loyalty`,
      icon: Award,
      roles: ['owner', 'admin', 'manager'] as TenantRole[]
    }, {
      name: 'Redimir Beneficios',
      path: `/admin/${slug}/loyalty/redemption`,
      icon: Gift,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    }, {
      name: 'Mi menú digital QR',
      path: `/admin/${slug}/menu-qr`,
      icon: QrCode,
      roles: ['owner', 'admin', 'manager'] as TenantRole[]
    }]
  }];
  const isCaprichoAdmin = String(restaurant.id) === '2' || restaurant.slug === 'capricho-de-canela';
  const isCongeladosAdmin = String(restaurant.id) === '18' || restaurant.slug === 'heladeria-congelados';
  const caprichoHidden = ['Tomar Orden', 'Mesas', 'Estaciones'];
  const congeladosHidden = ['Tomar Orden', 'Mesas', 'Estaciones'];
  // super_admin and platform_admin have full access (same as owner)
  const effectiveRole: TenantRole = role === 'super_admin' || role === 'platform_admin' ? 'owner' : role;
  // Filter entries by role + Capricho hides
  const filteredEntries: NavEntry[] = allEntries.filter(entry => entry.roles.includes(effectiveRole)).filter(entry => !(isCaprichoAdmin && caprichoHidden.includes(entry.name))).filter(entry => !(isCongeladosAdmin && congeladosHidden.includes(entry.name))).map(entry => {
    if (isGroup(entry)) {
      return {
        ...entry,
        items: entry.items.filter(it => it.roles.includes(effectiveRole))
      };
    }
    return entry;
  })
  // Hide groups that ended up empty after role filter
  .filter(entry => !isGroup(entry) || entry.items.length > 0);
  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    } else {
      navigate(`/admin/${restaurant.slug}/login`);
    }
  };
  const isItemActive = (path: string) => {
    const itemUrl = new URL(path, window.location.origin);
    const currentUrl = new URL(location.pathname + location.search, window.location.origin);
    return path.includes('?') ? itemUrl.pathname === currentUrl.pathname && itemUrl.search === currentUrl.search : location.pathname === path && !location.search;
  };
  const renderNavItem = (item: NavItem, nested = false) => {
    const isActive = isItemActive(item.path);
    return <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={cn('flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200', nested && 'ml-3 py-2.5', isActive ? 'bg-brand-charcoal text-white shadow-md shadow-gray-900/10' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')} data-id="element-1614">
        <item.icon className={cn('w-5 h-5 shrink-0', isActive ? 'text-white' : 'text-gray-400')} data-id="element-1615" />
        <span className="flex-1 truncate text-sm" data-id="element-1616">{item.name}</span>
        {item.badge !== undefined && item.badge > 0 && <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold flex items-center justify-center shrink-0', isActive ? 'bg-white text-brand-charcoal' : 'bg-red-500 text-white')} data-id="element-1617">
            {item.badge}
          </span>}
      </Link>;
  };
  const renderGroup = (group: NavGroup) => {
    const isOpen = openGroups[group.name] ?? false;
    const hasActiveChild = group.items.some(it => isItemActive(it.path));
    const groupBadgeTotal = group.items.reduce((sum, it) => sum + (it.badge || 0), 0);
    return <div key={group.name} className="space-y-1" data-id="element-1618">
        <button type="button" onClick={() => setOpenGroups(prev => ({
        ...prev,
        [group.name]: !isOpen
      }))} className={cn('w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200', hasActiveChild ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')} data-id="element-1619">
          <group.icon className="w-5 h-5 shrink-0 text-gray-400" data-id="element-1620" />
          <span className="flex-1 text-left truncate text-sm" data-id="element-1621">
            {group.name}
          </span>
          {groupBadgeTotal > 0 && !isOpen && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 bg-red-500 text-white" data-id="element-1622">
              {groupBadgeTotal}
            </span>}
          <ChevronDown className={cn('w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0', isOpen && 'rotate-180')} data-id="element-1623" />
        </button>
        {isOpen && <div className="space-y-1 pl-2 border-l border-gray-100 ml-4" data-id="element-1624">
            {group.items.map(it => renderNavItem(it, true))}
          </div>}
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans" data-id="element-1625">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-sm" data-id="element-1626">
        <div className="flex items-center gap-3" data-id="element-1627">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100" data-id="element-1628" /> : <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center" data-id="element-1629">
              <Store className="w-4 h-4 text-white" data-id="element-1630" />
            </div>}
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-1631">
            {restaurant.name}
          </span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1632">
          {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-1633" /> : <Menu className="w-6 h-6" data-id="element-1634" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={cn('fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 flex flex-col shadow-xl md:shadow-none', isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full')} data-id="element-1635">
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-100 flex items-center gap-3" data-id="element-1636">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-10 h-10 rounded-xl object-cover shadow-sm border border-gray-100" data-id="element-1637" /> : <div className="w-10 h-10 bg-brand-charcoal rounded-xl flex items-center justify-center shadow-sm" data-id="element-1638">
              <Store className="w-5 h-5 text-white" data-id="element-1639" />
            </div>}
          <div className="flex-1 min-w-0" data-id="element-1640">
            <h2 className="font-bold text-gray-900 truncate text-lg" data-id="element-1641">
              {restaurant.name}
            </h2>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-1642">
              {role}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1.5" data-id="element-1643">
          {filteredEntries.map(entry => isGroup(entry) ? renderGroup(entry) : renderNavItem(entry))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-100" data-id="element-1644">
          <button onClick={handleSignOut} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-id="element-1645">
            <LogOut className="w-5 h-5 text-gray-400 group-hover:text-red-500" data-id="element-1646" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-30 md:hidden transition-opacity" onClick={() => setIsMobileMenuOpen(false)} data-id="element-1647" />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#FAFAFA]" data-id="element-1648">
        <div className="flex-1 overflow-y-auto" data-id="element-1649">{children}</div>
      </main>
    </div>;
}